import type {
  AnswerSheetConfig, DescriptiveAnswer, DescriptivePartAnswer, SheetType, StrokesBlock, TextBlock
} from '../types/models';

/** The provisioning used when an exam has no explicit answer-sheet config (legacy / default). */
export const DEFAULT_SHEET_CONFIG: AnswerSheetConfig = { mainSheet: 'ruled', mainPages: 1, extras: [] };

// A4 portrait (210 × 297 mm) at 96 DPI, used as the logical coordinate space for every ink
// page. Storing strokes in this device-independent space (rather than screen pixels) means a
// page renders identically on a tablet, a desktop and on paper, and the rasterised render fed
// to AI grading is always a single, predictably-sized A4 sheet.
export const A4_LOGICAL_WIDTH = 794;
export const A4_LOGICAL_HEIGHT = 1123;

// Floor height for any page (legacy pages saved before full-A4 pages keep loading at >= this).
export const PAGE_MIN_HEIGHT = 480;

export function emptyStrokesPage(sheetType: SheetType = 'ruled', questionNo?: string): StrokesBlock {
  return {
    type: 'strokes',
    id: crypto.randomUUID(),
    width: A4_LOGICAL_WIDTH,
    // Start at full A4 so the student gets a real answer-sheet-sized box up front; when it's
    // full they add another page at the bottom.
    height: A4_LOGICAL_HEIGHT,
    strokes: [],
    sheetType,
    ...(questionNo ? { questionNo } : {}),
  };
}

/** The main pages an answer (or one part) starts with, per the exam's sheet config. */
function initialMainPages(cfg?: AnswerSheetConfig | null, count = 1): StrokesBlock[] {
  const main: SheetType = cfg?.mainSheet ?? 'ruled';
  const n = Math.max(1, count);
  return Array.from({ length: n }, () => emptyStrokesPage(main));
}

export function emptyDescriptive(_allowText: boolean, cfg?: AnswerSheetConfig | null): DescriptiveAnswer {
  return {
    kind: 'descriptive',
    pages: initialMainPages(cfg, cfg?.mainPages ?? 1),
    blocks: [],
  };
}

/** A blank answer for a multi-part descriptive question: one slot per question part. Each part
 *  starts on a single main sheet (the per-answer page count applies to single-answer questions). */
export function emptyDescriptiveForParts(
  parts: { id: string }[], _allowText: boolean, cfg?: AnswerSheetConfig | null
): DescriptiveAnswer {
  return {
    kind: 'descriptive',
    pages: [],
    blocks: [],
    parts: parts.map(p => ({
      partId: p.id,
      pages: initialMainPages(cfg, 1),
      blocks: [],
    })),
  };
}

function normalizePages(raw: any): StrokesBlock[] {
  let pages: StrokesBlock[] = [];
  if (Array.isArray(raw?.pages)) {
    pages = raw.pages.filter((p: any) => p && Array.isArray(p.strokes));
  } else if (raw?.strokes && Array.isArray(raw.strokes.strokes)) {
    pages = [raw.strokes];
  }
  if (pages.length === 0) pages = [emptyStrokesPage()];

  return pages.map(p => ({
    ...p,
    type: 'strokes' as const,
    id: p.id || crypto.randomUUID(),
    width: p.width || A4_LOGICAL_WIDTH,
    height: Math.max(PAGE_MIN_HEIGHT, p.height || PAGE_MIN_HEIGHT),
    strokes: p.strokes,
    // Pre-sheetType pages only carry the `ruled` flag — derive the sheet from it.
    sheetType: (p.sheetType ?? (p.ruled === false ? 'plain' : 'ruled')) as SheetType,
  }));
}

function normalizeBlocks(raw: any): TextBlock[] {
  return Array.isArray(raw?.blocks)
    ? raw.blocks.filter((b: any) => b && b.type === 'text')
    : [];
}

/**
 * Coerce a parsed answer payload into the current DescriptiveAnswer shape. Accepts both the
 * current multi-page shape (`pages`) and the legacy single-canvas shape (`strokes`), so
 * in-progress attempts saved before the A4/paging change keep loading. A legacy canvas may be
 * taller than A4; we keep its stored height as-is (lossless) — only new growth is capped.
 *
 * A multi-part answer carries a `parts` array; each entry is normalized independently and the
 * top-level pages/blocks are left empty (they aren't used when the question has parts).
 */
export function normalizeDescriptive(raw: any): DescriptiveAnswer {
  if (Array.isArray(raw?.parts)) {
    const parts: DescriptivePartAnswer[] = raw.parts
      .filter((p: any) => p && typeof p.partId === 'string')
      .map((p: any) => ({
        partId: p.partId,
        pages: normalizePages(p),
        blocks: normalizeBlocks(p),
      }));
    return { kind: 'descriptive', pages: [], blocks: [], parts };
  }

  return { kind: 'descriptive', pages: normalizePages(raw), blocks: normalizeBlocks(raw) };
}

/** Whether a descriptive answer (or one part) holds any ink or typed text. */
export function hasDescriptiveContent(a: { pages: StrokesBlock[]; blocks: TextBlock[] }): boolean {
  return a.pages.some(p => p.strokes.length > 0) || a.blocks.some(b => b.value.trim().length > 0);
}

/** Whether a (possibly multi-part) descriptive answer holds any content at all. */
export function descriptiveAnswered(a: DescriptiveAnswer): boolean {
  if (a.parts && a.parts.length > 0) return a.parts.some(hasDescriptiveContent);
  return hasDescriptiveContent(a);
}
